<script setup lang="ts">
  import { onMounted, watch } from 'vue'
  import { useRoute } from 'vue-router'
  import bgSidebar from '@/assets/images/bg-sidebar.jpg'
  import { useAuthLogout } from '@/models/auth'
  import { useAuthStore } from '@/stores/auth'
  import SidebarHeader from './sidebar-header.vue'
  import SidebarItems from './sidebar-items.vue'
  import SidebarSearch from './sidebar-search.vue'

  const props = defineProps<{
    show: boolean,
  }>()

  const emit = defineEmits<{
    (e: 'toggleSidebarShow'): void,
    (e: 'toggleSidebarSize'): void,
    (e: 'openSearch'): void,
  }>()

  // Set the CSS custom property with the imported image
  onMounted(() => {
    document.documentElement.style.setProperty('--sidebar-background-image', `url(${bgSidebar})`)
  })

  const route = useRoute()
  watch(
    () => route.fullPath,
    () => {
      if (props.show) {
        emit('toggleSidebarShow')
      }
    },
  )

  const auth = useAuthStore()
  const logout = useAuthLogout()
</script>

<template>
  <div>
    <div
      class="sidebar-dimmer"
      @click="emit('toggleSidebarShow')"></div>

    <nav class="sidebar">
      <sidebar-header
        class="sidebar-header"
        @toggle-sidebar-show="emit('toggleSidebarShow')"
        @toggle-sidebar-size="emit('toggleSidebarSize')" />

      <sidebar-search
        class="lg:hidden px-3.5 lg:mb-0 mb-4"
        @open-search="emit('openSearch')" />

      <hr>

      <sidebar-items class="sidebar-content" />

      <div class="px-4 py-2">
        <div class="flex rounded-lg items-center gap-4 bg-gray-500/10 py-2 px-4 text-white relative">
          <div>
            <div class="size-10 rounded-full bg-white"></div>
          </div>

          <div class="flex-1 min-w-0">
            <div class="truncate">
              {{ auth.user.name }}
            </div>
            <div class="text-xs">
              {{ auth.user.role.name }}
            </div>
          </div>

          <app-button
            title="Keluar"
            class="text-xl leading-0"
            @click="logout()">
            <fa-icon icon="i-far-arrow-right-from-bracket" />
          </app-button>
        </div>
      </div>
    </nav>
  </div>
</template>

<style>
@reference "@/assets/styles/main.css";

:root {
  /* --sidebar-background-image: url("@/assets/images/bg-sidebar.jpg"); */
  --sidebar-width: 18rem; /* 18rem 284px */
}

.sidebar {
  @apply text-white;
  @apply fixed inset-y-0 flex max-w-full flex-col overflow-x-hidden;
  @apply left-[calc(var(--sidebar-width)*-1)];
  @apply w-[var(--sidebar-width)] lg:left-0 z-30;
  @apply transition-all duration-500;

  background-size: cover;
  background-image: var(--sidebar-background-image);
  background-color: rgb(27 27 27 / 60%);
  background-blend-mode: multiply; /* or 'multiply' */
  background-position-x: -50px;

  .sidebar-show & {
    left: 0;
    box-shadow:
      0 16px 38px -12px rgb(0 0 0 / 56%),
      0 4px 25px 0 rgb(0 0 0 / 12%),
      0 8px 10px -5px rgb(0 0 0 / 20%);
  }
}

.sidebar-header,
.sidebar hr,
.sidebar-dimmer,
.sidebar-content {
  @apply z-20;
}

.sidebar-header {
  @apply h-[var(--navbar-height)] px-4;
}

.sidebar-dimmer {
  @apply fixed inset-0;
  @apply bg-black/0;
  @apply transition-all duration-200;
  @apply pointer-events-none;
}

.sidebar-content {
  @apply mt-5 flex-1 overflow-y-auto overflow-x-hidden pb-6 scrollbar-none;
  @apply px-4;
}

.sidebar-show .sidebar-dimmer {
  @apply bg-black/40 lg:bg-black/0;
  @apply max-lg:pointer-events-auto;
}

.sidebar-group-label {
  @apply mb-1 mt-3 whitespace-nowrap text-sm font-bold pl-1;
  @apply transition-opacity duration-200;
}

.sidebar-item-icon {
  @apply w-8;
}
.sidebar-company-icon {
  @apply h-8;
}
</style>
